import {closeCurrentDialog, openDialog, showNotification} from 'platform/components';
import {Text} from 'platform/foundation';

import {innerJoin, isNil, isNotNil} from 'ramda';
import {isEmptyArray, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {useGetBranchQuery, useGetParticipationQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useChangeBranchSaleVehicleMutation,
  useChangeRentalVehicleBranchMutation,
  useGetRentalVehicleQuery,
  useGetSaleVehicleQuery,
} from '@dms/api/vehicle';
import {
  useLazyListAvailableVehicleWarehousesForVehicleMovementQuery,
  useLazyListVehicleWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {TestIdProps} from 'shared/web';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {useVehicleWarehouse} from '../../../hooks/useVehicleWarehouse/useVehicleWarehouse';
import {handleApiError} from '../../../utils/handleApiError';
import {BranchChanger} from './BranchChanger';

export interface LocationProps extends TestIdProps {
  recordId: string;
}

export function VehicleLocation(props: LocationProps) {
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.recordId});
  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId: props.recordId});
  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId: props.recordId});
  const {data: currentUserInfo} = useGetCurrentUserInfoQuery();
  const [changeBranchSaleVehicle, {isLoading: isBranchChangeLoading}] =
    useChangeBranchSaleVehicleMutation();
  const [changeRentalVehicleBranch, {isLoading: isRentalBranchChangeLoading}] =
    useChangeRentalVehicleBranchMutation();
  const [getAvailableWarehouses, {isFetching: isAvailableFetching}] =
    useLazyListAvailableVehicleWarehousesForVehicleMovementQuery();
  const [getVehicleWarehouseList, {isFetching: isListFetching}] =
    useLazyListVehicleWarehousesQuery();

  const {data: vehicleParticipationId} = useGetParticipationQuery({
    recordId: props.recordId,
    resourceId: EntityResourceIds.vehicle,
  });

  const [canChangeVehicleBranch] = usePermissions({
    permissionKeys: ['vehicleChangeBranch'],
    scopes: {
      vehicleChangeBranch: {participation: vehicleParticipationId},
    },
  });

  const {availableActions, isVehicleWarehouseEnabled, openTransferDialog} = useVehicleWarehouse({
    vehicleId: props.recordId,
    saleVehicleId: saleVehicle?.id,
    vehicleWarehouseId: saleVehicle?.vehicleWarehouse?.id,
    onMovementComplete: (branchId) => {
      if (isNotNilOrEmpty(branchId) && isString(branchId)) {
        updateVehicleBranch(branchId);
      }
    },
    'data-testid': props['data-testid'],
  });

  const userAccessibleBranch = currentUserInfo?.branches?.find(
    (branch) => branch.id === vehicle?.branchId
  );

  const {data: branchById, isLoading: isBranchLoading} = useGetBranchQuery(
    {branchId: vehicle?.branchId ?? ''},
    {skip: isNil(vehicle?.branchId) || isNotNil(userAccessibleBranch)}
  );

  const activeBranch = userAccessibleBranch ?? branchById;
  const hasRentalVehicle = isNotNil(rentalVehicle);

  const isLoading =
    isBranchChangeLoading ||
    isAvailableFetching ||
    isListFetching ||
    isBranchLoading ||
    isRentalBranchChangeLoading;

  const updateVehicleBranch = (branchId: string) => {
    if (hasRentalVehicle) {
      changeRentalVehicleBranch({
        vehicleId: props.recordId,
        changeRentalVehicleBranchRequestBody: {branchId},
      })
        .unwrap()
        .catch(handleApiError);
    }

    changeBranchSaleVehicle({
      vehicleId: props.recordId,
      changeBranchSaleVehicleRequestBody: {branchId},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleUpdateVehicleBranch = (branchId: string) => {
    if (!isVehicleWarehouseEnabled || isNil(saleVehicle?.vehicleWarehouse?.id)) {
      return updateVehicleBranch(branchId);
    }

    if (isNotNil(saleVehicle?.vehicleWarehouse?.id) && !availableActions?.transfer) {
      showNotification.error(i18n.t('entity.vehicleWarehouse.notifications.transferNotAllowed'));

      return;
    }

    getVehicleWarehouseList({branchId})
      .unwrap()
      .then((list) => {
        if (list.some((warehouse) => warehouse.id === saleVehicle?.vehicleWarehouse?.id)) {
          return updateVehicleBranch(branchId);
        }

        return getAvailableWarehouses({vehicleId: props.recordId})
          .unwrap()
          .then((warehouses) => {
            const availableWarehouses = innerJoin(
              (warehouseByUser, warehouseByBranch) => warehouseByUser.id === warehouseByBranch.id,
              warehouses,
              list
            );

            if (isEmptyArray(availableWarehouses)) {
              openDialog(
                <Text>{i18n.t('entity.vehicleWarehouse.labels.noWarehouseForBranch')}</Text>,
                {
                  size: 'small',
                  buttons: [
                    {
                      title: i18n.t('general.actions.close'),
                      variant: 'secondary',
                      onClick: closeCurrentDialog,
                    },
                  ],
                }
              );

              return;
            }

            openTransferDialog(branchId);
          });
      })
      .catch(handleApiError);
  };

  return (
    <BranchChanger
      recordId={props.recordId}
      activeBranch={activeBranch}
      onChange={handleUpdateVehicleBranch}
      isLoading={isLoading}
      data-testid={props['data-testid']}
      isEditDisabled={!canChangeVehicleBranch}
      tooltip={i18n.t('entity.controls.labels.vehicle_location.tooltip')}
    />
  );
}
